import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Trash2 } from 'lucide-react';
import { criarPedido } from './api';
import { dataHojeISO, usePedidosStore } from './store';
import { useToast } from '@/components/Toast';
import Button from '@/components/Button';
import Input from '@/components/Input';
import { cn } from '@/lib/cn';
import { lockBody, unlockBody } from '@/lib/bodyLock';

interface Props {
  aberto: boolean;
  onClose: () => void;
}

interface ItemForm {
  descricao: string;
  qtd: number;
}

const PAGAMENTOS = ['Pix', 'Dinheiro', 'Cartão', 'Crypto'];

export function NovoPedidoDialog({ aberto, onClose }: Props) {
  const upsert = usePedidosStore((s) => s.upsert);
  const toast = useToast();

  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [endereco, setEndereco] = useState('');
  const [total, setTotal] = useState('');
  const [pagamento, setPagamento] = useState<string>('Pix');
  const [observacoes, setObservacoes] = useState('');
  const [mapaLink, setMapaLink] = useState('');
  const [itens, setItens] = useState<ItemForm[]>([{ descricao: '', qtd: 1 }]);
  const [enviando, setEnviando] = useState(false);
  const [erros, setErros] = useState<{ nome?: string; endereco?: string }>({});

  useEffect(() => {
    if (!aberto) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    lockBody();
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      unlockBody();
    };
  }, [aberto, onClose]);

  function reset() {
    setNome('');
    setTelefone('');
    setEndereco('');
    setTotal('');
    setPagamento('Pix');
    setObservacoes('');
    setMapaLink('');
    setItens([{ descricao: '', qtd: 1 }]);
    setErros({});
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errs: typeof erros = {};
    if (!nome.trim()) errs.nome = 'Nome é obrigatório.';
    if (!endereco.trim()) errs.endereco = 'Endereço é obrigatório.';
    if (Object.keys(errs).length) {
      setErros(errs);
      return;
    }
    setErros({});
    setEnviando(true);
    try {
      const itensValidos = itens
        .filter((i) => i.descricao.trim())
        .map((i) => ({ descricao: i.descricao.trim(), qtd: i.qtd }));
      const pedido = await criarPedido({
        nome_cliente: nome.trim(),
        telefone: telefone.trim() || null,
        endereco_entrega: endereco.trim(),
        total: total.trim() || null,
        pagamento,
        observacoes: observacoes.trim() || null,
        mapa_link: mapaLink.trim() || null,
        itens: itensValidos,
      });
      // só upsert local se filtro atual permite (hoje ou 'all')
      const filtro = usePedidosStore.getState().data;
      if (filtro === 'all' || filtro === dataHojeISO()) {
        upsert({ ...pedido, itens: itensValidos });
      }
      toast('success', `${pedido.numero_ticket} criado.`);
      reset();
      onClose();
    } catch (err) {
      toast('error', `Falha ao criar — ${(err as Error).message}.`);
    } finally {
      setEnviando(false);
    }
  }

  return createPortal(
    <>
      <div
        onClick={onClose}
        className={cn(
          'fixed inset-0 z-[100] transition-opacity duration-base',
          aberto ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
        )}
        style={{ background: 'rgba(7,9,10,0.55)', backdropFilter: 'blur(8px)' }}
      />
      <div
        className={cn(
          'fixed inset-0 z-[101] flex items-center justify-center p-s5 transition-opacity duration-base',
          aberto ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
        )}
        aria-hidden={!aberto}
      >
        <form
          onSubmit={onSubmit}
          className={cn(
            'w-[640px] max-w-[96vw] max-h-[90vh] flex flex-col bg-elev border border-[var(--border-strong)] rounded-xl overflow-hidden',
            'transition-transform duration-slow ease-out-expo',
            aberto ? 'translate-y-0 scale-100' : 'translate-y-[2vh] scale-[0.98]',
          )}
          style={{ boxShadow: '0 40px 100px rgba(0,0,0,0.7), 0 16px 40px rgba(0,0,0,0.5)' }}
        >
          <header className="flex items-center justify-between px-s7 py-s5 border-b border-[var(--border-subtle)]">
            <div>
              <span className="t-overline">Novo pedido</span>
              <h2 className="font-display text-[28px] leading-tight text-fg-1 tracking-[-0.02em] mt-s1">
                Anotar entrega
              </h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-s2 rounded-sm border border-transparent text-fg-3 hover:text-fg-1 hover:border-[var(--border)]"
              aria-label="fechar"
            >
              <X size={18} strokeWidth={1.5} />
            </button>
          </header>

          <div className="flex-1 overflow-y-auto px-s7 py-s5 flex flex-col gap-s5">
            <Campo label="Cliente">
              <Input
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Nome do cliente"
                error={erros.nome}
                autoFocus
              />
            </Campo>

            <div className="grid grid-cols-2 gap-s4">
              <Campo label="Telefone">
                <Input
                  value={telefone}
                  onChange={(e) => setTelefone(e.target.value)}
                  placeholder="62 9 9999-9999"
                />
              </Campo>
              <Campo label="Pagamento">
                <select
                  value={pagamento}
                  onChange={(e) => setPagamento(e.target.value)}
                  className="bg-input border border-[var(--border)] rounded-sm px-s4 py-[9px] font-body text-[13.5px] text-fg-1 outline-none transition-all duration-fast ease-soft hover:border-[var(--border-strong)] focus:border-moss-400 focus:bg-card"
                >
                  {PAGAMENTOS.map((p) => (
                    <option key={p} value={p} className="bg-elev text-fg-1">
                      {p}
                    </option>
                  ))}
                </select>
              </Campo>
            </div>

            <Campo label="Endereço de entrega">
              <Input
                value={endereco}
                onChange={(e) => setEndereco(e.target.value)}
                placeholder="Rua, número, bairro, cidade-UF"
                error={erros.endereco}
              />
            </Campo>

            <Campo label="Link do Google Maps (opcional, mais preciso que o endereço)">
              <Input
                value={mapaLink}
                onChange={(e) => setMapaLink(e.target.value)}
                placeholder="https://www.google.com/maps?q=-16.70,-49.25"
              />
            </Campo>

            <Campo label="Total">
              <Input
                value={total}
                onChange={(e) => setTotal(e.target.value)}
                placeholder="180,00"
              />
            </Campo>

            <section className="flex flex-col gap-s2">
              <div className="flex items-center justify-between">
                <span className="t-overline">Itens</span>
                <button
                  type="button"
                  onClick={() => setItens([...itens, { descricao: '', qtd: 1 }])}
                  className="inline-flex items-center gap-s2 text-[12px] text-moss-300 hover:text-moss-200"
                >
                  <Plus size={12} strokeWidth={1.6} /> Adicionar
                </button>
              </div>
              {itens.map((it, i) => (
                <div key={i} className="grid grid-cols-[1fr_72px_36px] gap-s2">
                  <Input
                    value={it.descricao}
                    onChange={(e) => {
                      const cp = [...itens];
                      cp[i] = { ...cp[i], descricao: e.target.value };
                      setItens(cp);
                    }}
                    placeholder="Buquê de tulipas brancas"
                  />
                  <Input
                    type="number"
                    min={1}
                    value={it.qtd}
                    onChange={(e) => {
                      const cp = [...itens];
                      cp[i] = { ...cp[i], qtd: Math.max(1, Number(e.target.value) || 1) };
                      setItens(cp);
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setItens(itens.length === 1 ? itens : itens.filter((_, j) => j !== i))}
                    disabled={itens.length === 1}
                    className="rounded-sm border border-[var(--border)] text-fg-3 hover:text-petal-300 hover:border-petal-700 disabled:opacity-30 transition-colors duration-fast"
                    aria-label="remover item"
                  >
                    <Trash2 size={14} strokeWidth={1.6} className="mx-auto" />
                  </button>
                </div>
              ))}
            </section>

            <Campo label="Observações (opcional)">
              <textarea
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                placeholder="Mensagem do cartão, instruções de entrega…"
                className="bg-input border border-[var(--border)] rounded-sm px-s4 py-[9px] font-body text-[13.5px] text-fg-1 outline-none transition-all duration-fast ease-soft hover:border-[var(--border-strong)] focus:border-moss-400 focus:bg-card min-h-[72px] resize-y"
              />
            </Campo>
          </div>

          <footer className="flex justify-end gap-s3 px-s7 py-s4 border-t border-[var(--border-subtle)]">
            <Button type="button" variant="ghost" onClick={onClose} disabled={enviando}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={enviando}>
              {enviando ? 'Salvando…' : 'Criar pedido'}
            </Button>
          </footer>
        </form>
      </div>
    </>,
    document.body,
  );
}

function Campo({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-s2">
      <span className="t-overline">{label}</span>
      {children}
    </label>
  );
}

export default NovoPedidoDialog;
